import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-regular-svg-icons";
import { faCheck, faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import "./notificationItem.scss";

// notification_type → 카테고리 매핑 (BE buccl_user/notification_config.py 기준)
// 예약/배송에 해당하지 않는 모든 타입(리마인더·취소·결제요청·미정의)은 reminder로 폴백한다.
const RESERVATION_TYPES = new Set([
    "WAITING_CONFIRMED",
    "NEW_RESERVATION",
    "PRACTICE_WAITING_CONFIRMED",
    "NEW_PRACTICE_RESERVATION",
    "TRAVEL_ORDER_INSTRUCTOR",
]);
const DELIVERY_TYPES = new Set(["PRODUCT_SHIPPED", "PRODUCT_DELIVERED"]);

// 카테고리별 아이콘/스타일 (색상은 notificationItem.scss의 modifier 클래스에서 처리)
const CATEGORY_META = {
    reservation: { icon: faCheck, className: "is-reservation" },
    delivery: { icon: faShoppingCart, className: "is-delivery" },
    reminder: { icon: faBell, className: "is-reminder" },
};

const getCategory = (notificationType) => {
    if (RESERVATION_TYPES.has(notificationType)) return "reservation";
    if (DELIVERY_TYPES.has(notificationType)) return "delivery";
    return "reminder"; // 미정의 타입 포함 폴백
};

/**
 * Notification Item Component
 * @param {object} notification - Notification data
 * @param {string} notification.id - Notification ID
 * @param {string} notification.title - Notification title
 * @param {string} notification.message - Notification message
 * @param {string} notification.sender - Who sent the notification
 * @param {string} notification.created_at - When notification was sent
 * @param {boolean} notification.is_read - Read status
 * @param {Array<{id:string,label:string,deeplink?:string,api?:string}>} [notification.actions] - Inline action buttons
 * @param {function} onClick - Card click handler
 * @param {function} [onActionClick] - Inline action button handler: (notification, action) => void
 */
const NotificationItem = ({ notification, onClick, onActionClick }) => {
    const { title, message, sender, created_at, is_read, notification_type, actions } = notification;
    const { icon, className: categoryClass } = CATEGORY_META[getCategory(notification_type)];

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        const diffInHours = Math.floor(diffInMinutes / 60);
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInMinutes < 1) return "방금 전";
        if (diffInMinutes < 60) return `${diffInMinutes}분 전`;
        if (diffInHours < 24) return `${diffInHours}시간 전`;
        if (diffInDays < 7) return `${diffInDays}일 전`;

        return date.toLocaleDateString("ko-KR", {
            month: "long",
            day: "numeric",
        });
    };

    const hasActions = Array.isArray(actions) && actions.length > 0;

    return (
        <div
            className={`notification-item ${is_read ? "read" : "unread"}`}
            onClick={() => onClick(notification)}
        >
            {!is_read && <span className="unread-indicator"></span>}

            <div className={`notification-icon ${categoryClass}`}>
                <FontAwesomeIcon icon={icon} />
            </div>

            <div className="notification-content">
                <div className="notification-header">
                    <h4 className="notification-title">{title}</h4>
                    <span className="notification-time">
                        {formatTime(created_at)}
                    </span>
                </div>

                <p className="notification-body">{message}</p>

                <div className="notification-footer">
                    <span className="notification-sender">{sender}</span>
                </div>

                {hasActions && (
                    <div className="notification-actions">
                        {actions.map((action, idx) => (
                            <button
                                key={action.id || idx}
                                type="button"
                                className={`notification-action-btn ${idx === 0 ? "primary" : "ghost"}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onActionClick?.(notification, action);
                                }}
                            >
                                {action.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationItem;
